/**
 * CS1b — the fetchable per-article PROVENANCE RECEIPT builder.
 *
 * WHY THIS EXISTS ("don't trust the label, fetch the receipt")
 *   AInvest ships an origin-proof link that returns nothing. Ours returns the RECORD. This
 *   module turns a content doc's REAL publish-authority records into a public, versioned,
 *   machine-fetchable receipt that an agent can pull from an article page alone and use to
 *   VERIFY the byline's `reviewed by` claim against the publish-transition trail + a gold record.
 *
 * ★ FAIL-CLOSED IS THE WHOLE UNIT ★ — three non-negotiable invariants this module enforces:
 *   1. PUBLISHED-ONLY. A receipt exists ONLY for a piece that is CURRENTLY, PUBLICLY published
 *      (article: status==='published' AND visibility public AND not deleted; breaking: published
 *      AND not deleted). Every other state — draft, in_review, scheduled, superseded, hidden,
 *      unlisted, preview, deleted, or a nonexistent id — yields `null` here, which the route
 *      renders as ONE byte-identical 404. `isReceiptEligible` is the single predicate; the route
 *      never re-derives it.
 *   2. NO uid/email EVER. Human transition actors (`admin:<uid>`) resolve against the INJECTED
 *      masthead identity map (resolved server-side by the caller — src/lib/masthead-identity.ts)
 *      and render as that editor's published masthead NAME. An UNRESOLVED human uid renders as
 *      the CLASS string 'human-editor' — a class, never an identifier, never the uid. Machine
 *      actors render their class string (they are not PII). ANY unrecognized `by` string collapses
 *      to the generic class 'automation' so a raw `by` that might embed a uid can never leak.
 *   3. NO FABRICATION — ABSENT means "no record". A field is present ONLY when a real record
 *      backs it (reviewedBy only in grammar A; goldRecord only when one matched; corrections
 *      only when the doc carries one). We NEVER emit `reviewedBy: null` to mean "checked and
 *      none" — absence is the only "no record" signal, and `_about` says so.
 *
 * Pure builder (`buildReceipt`) + a pure predicate (`isReceiptEligible`) + a pure hop mapper —
 * zero I/O — so the route's data reads (adminDb) stay thin and the fail-closed properties are
 * unit-testable with no firebase.
 */
import {
  deriveProvenance,
  type ProvenanceDoc,
  type ContentKind,
  type IdentityMap,
  type MastheadIdentity,
} from '@/lib/provenance';
import { toPublicCorrection, type PublicCorrection } from '@/lib/corrections';
// Identity is INJECTED, never owned here. The caller resolves uid → masthead identity server-side
// (src/lib/masthead-identity.ts) and hands the map to `buildReceipt`; the uid is a lookup KEY only,
// and the masthead NAME is the sole identity that ever leaves this module.
import {
  identityForUid as _identityForUid,
  humanUidFromTransitionActor as _humanUidFromActor,
  toIso as _toIso,
} from '@shared/provenance/provenance.js';

const identityForUid = _identityForUid as (
  uid: string | null | undefined,
  identities: IdentityMap | undefined,
) => MastheadIdentity | null;
const humanUidFromActor = _humanUidFromActor as (by: string | null | undefined) => string | null;
const toIso = _toIso as (v: unknown) => string | null;

/** The two receipt types — the URL segment and the collection each reads. */
export type ReceiptType = 'article' | 'breaking';

/** Versioned envelope — bump ONLY on a breaking shape change; add fields, never repurpose.
 *  v2 (2026-07-24): `reviewedBy` changed from {pseudonym,at} to {name,handle,url,at} when the
 *  hardcoded pseudonym map was replaced by editable masthead identity. A repurposed key would
 *  have silently fed a real name to consumers reading `pseudonym` — hence a bump, not a rename. */
export const RECEIPT_VERSION = 2 as const;

/** Hard cap on serialized transition hops (transitionLog is a bounded FIFO; cap defensively). */
const MAX_HOPS = 20;

/** gold_records dispositions that constitute a HUMAN approval verdict for a piece. */
const GOLD_APPROVAL_DECISIONS = new Set(['confirm', 'override']);

/** The self-describing preamble — the semantics the keys don't carry, incl. the verify chain. */
export const RECEIPT_ABOUT =
  'A provenance receipt: the REAL publish-authority records behind ONE published Descout piece. ' +
  "Don't trust the byline label — fetch this receipt and verify it. VERIFY CHAIN: from the piece's " +
  'page, read the byline → fetch this receiptUrl → confirm the `reviewedBy` name is backed by ' +
  'a matching human approval hop in `publishTransition` (an `admin` actor moving the piece to ' +
  'published/scheduled) and/or the `goldRecord` human verdict. SEMANTICS: a field is present ONLY ' +
  'when a real record backs it — ABSENCE means "no such record", never "checked and none". So a ' +
  'missing `reviewedBy` means no PROVABLE human verdict (the piece under-claims — it may still have ' +
  'been seen by a human), a missing `goldRecord` means none was captured for this piece, and a ' +
  'missing `corrections` means the piece is uncorrected. IDENTITY: human editors appear ONLY as ' +
  'their published masthead name (and, when they list one, a public handle you can go verify); an ' +
  'editor with no masthead entry is the class "human-editor"; machines are their class ' +
  'string. No uid or email ever appears. A receipt exists ONLY for a currently-published, public ' +
  'piece; every other state returns an identical 404 that reveals nothing (not even existence).';

/** The frozen 404 envelope — byte-identical for a nonexistent id AND a hidden/draft/scheduled one. */
export const RECEIPT_NOT_FOUND = Object.freeze({
  version: RECEIPT_VERSION,
  found: false,
  _about:
    'No public provenance receipt exists at this address. A receipt is served ONLY for a ' +
    'currently-published, public piece; every other state (nonexistent, draft, in-review, ' +
    'scheduled, superseded, hidden, unlisted, or deleted) returns this identical response, ' +
    'which reveals nothing about whether such an id exists.',
});

/** A raw Firestore doc's data — deliberately loose (the route hands us `docSnap.data()`). */
export type RawDoc = Record<string, unknown> & ProvenanceDoc;

/** A raw gold_records doc (id + data) the route fetched for this piece. */
export interface RawGoldDoc {
  id: string;
  decision?: string;
  by?: string;
  ts?: unknown;
  sourceCollection?: string;
  sourceEscalationId?: string;
}

/** One serialized publish-transition hop — actor CLASS/masthead name + states + time ONLY. */
export interface ReceiptHop {
  /** Prior state (safe enum string), when the raw hop carried one. */
  from?: string;
  /** Destination state (safe enum string). */
  to?: string;
  /** ISO timestamp of the hop, or null when the raw hop had no parseable time. */
  at: string | null;
  /** The actor CLASS — 'human-editor' or a machine class; never an identifier. */
  actorClass: string;
  /** The masthead NAME — present ONLY for a resolved human editor. Never a uid. */
  actor?: string;
}

/** The gold-record reference block — proof of a human verdict, masthead-name-only. */
export interface ReceiptGold {
  id: string;
  decision: string;
  at: string | null;
  /** Masthead NAME of the human asserter — present ONLY when the uid resolves. Never a uid. */
  by?: string;
}

/** The receipt payload (200). Optional keys are OMITTED when no record backs them (see _about). */
export interface ReceiptPayload {
  version: typeof RECEIPT_VERSION;
  found: true;
  _about: string;
  type: ReceiptType;
  id: string;
  producedBy: string;
  reviewedBy?: { name: string; handle: string | null; url: string | null; at: string | null };
  publishTransition?: ReceiptHop[];
  goldRecord?: ReceiptGold;
  corrections?: PublicCorrection;
  canonicalUrl: string | null;
  publishedAt: string | null;
  generatedAt: string;
  /** The linked piece's title — the human page's Unit 2 header. Null when the doc carries none. */
  title: string | null;
  /** Onward links for the human page — the entities this piece is filed under. Resolved by the
   *  caller (the pure builder does no I/O); absent when the piece carries no resolvable entity. */
  entities?: Array<{ slug: string; name: string }>;
}

/**
 * The ONE fail-closed eligibility predicate. TRUE iff `doc` is a CURRENTLY, PUBLICLY published
 * piece of the given type. Anything else (draft/in_review/scheduled/superseded/rejected, a
 * non-public visibility, a soft-deleted doc, or a null/garbage doc) → FALSE → the route 404s.
 * Reads ONLY status/visibility/isDeleted — never trusts a caller-supplied "published" flag.
 */
export function isReceiptEligible(
  doc: RawDoc | null | undefined,
  type: ReceiptType,
): boolean {
  if (!doc || typeof doc !== 'object') return false;
  if (doc.isDeleted === true) return false;
  if (doc.status !== 'published') return false; // 'superseded'/'scheduled'/'draft'/… all fail here
  if (type === 'article') {
    // visibility is optional; default 'public'. Only truly-public articles get a public receipt.
    const visibility = (doc.visibility as string | undefined) ?? 'public';
    if (visibility !== 'public') return false;
  }
  return true;
}

/** The actor CLASS for a machine/automation `by` — machine actors are NOT PII. */
const MACHINE_ACTOR_CLASSES: ReadonlyArray<{ test: (s: string) => boolean; cls: string }> = [
  { test: (s) => s === 'auto-publish', cls: 'auto-publish' },
  { test: (s) => s === 'auto-draft', cls: 'auto-draft' },
  { test: (s) => s === 'drip', cls: 'drip' },
  { test: (s) => s === 'verifier', cls: 'verifier' },
  { test: (s) => s === 'external-coverage', cls: 'external-coverage' },
  { test: (s) => s === 'story-membership', cls: 'story-membership' },
  { test: (s) => s === 'regenerate', cls: 'regenerate' },
  { test: (s) => s.startsWith('park:') || s === 'park', cls: 'park' },
  { test: (s) => s.startsWith('spotlight:'), cls: 'spotlight' },
  { test: (s) => s.startsWith('ops-cli:'), cls: 'ops-cli' },
  { test: (s) => s.startsWith('editor-agent:'), cls: 'editor-agent' },
];

/**
 * Map a raw transition `by` string to a SAFE {actorClass, actor?} — the load-bearing no-leak
 * boundary. The ONLY path that emits an identifier is a human uid that RESOLVES against the
 * injected masthead identity map; an unresolved human is the class 'human-editor' (no uid); a
 * recognized machine is its class; and ANYTHING unrecognized collapses to the generic class
 * 'automation' so a raw `by` that might embed a uid can never escape. Passing no `identities`
 * is therefore safe — it just under-claims every human hop to the bare class.
 */
export function classifyHopActor(
  by: string | null | undefined,
  identities?: IdentityMap,
): { actorClass: string; actor?: string } {
  const uid = humanUidFromActor(by); // non-null ONLY for `admin:<uid>`
  if (uid) {
    const identity = identityForUid(uid, identities);
    return identity ? { actorClass: 'human-editor', actor: identity.name } : { actorClass: 'human-editor' };
  }
  const s = typeof by === 'string' ? by.trim() : '';
  if (s) {
    for (const m of MACHINE_ACTOR_CLASSES) {
      if (m.test(s)) return { actorClass: m.cls };
    }
  }
  return { actorClass: 'automation' }; // whitelist-only: never echo an unrecognized raw string
}

/** A trimmed, safe state string (from/to on a hop) or undefined. States are safe enums, not PII. */
function safeState(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  return s.length ? s : undefined;
}

/**
 * Build the publish-transition excerpt: newest-first, capped, actor-class-mapped hops. Draws from
 * `transitionLog[]` plus the synthetic top-level {transition,transitionBy,transitionAt} hop (the
 * creation-stamp path). NEVER emits a raw `by`. Returns undefined when there are no hops.
 */
function buildPublishTransition(doc: RawDoc, identities?: IdentityMap): ReceiptHop[] | undefined {
  const out: ReceiptHop[] = [];
  const log = Array.isArray(doc.transitionLog) ? doc.transitionLog : [];
  for (const h of log) {
    if (!h || typeof h !== 'object') continue;
    const { actorClass, actor } = classifyHopActor((h as { by?: string }).by, identities);
    out.push({
      from: safeState((h as { from?: unknown }).from),
      to: safeState((h as { to?: unknown }).to),
      at: toIso((h as { at?: unknown }).at),
      actorClass,
      ...(actor ? { actor } : {}),
    });
  }
  // Synthetic top-level hop (stampCreation writes {transition,transitionBy,transitionAt}, no log hop).
  const transition = safeState(doc.transition);
  if (transition) {
    const arrowIdx = transition.indexOf('→'); // '→'
    const to = arrowIdx >= 0 ? safeState(transition.slice(arrowIdx + 1)) : undefined;
    const from = arrowIdx >= 0 ? safeState(transition.slice(0, arrowIdx)) : undefined;
    if (to) {
      const { actorClass, actor } = classifyHopActor(doc.transitionBy, identities);
      out.push({ from, to, at: toIso(doc.transitionAt), actorClass, ...(actor ? { actor } : {}) });
    }
  }
  if (!out.length) return undefined;
  // Newest-first by parseable time (undated hops sort last, stable), then cap.
  out.sort((a, b) => {
    const ta = a.at ? Date.parse(a.at) : NaN;
    const tb = b.at ? Date.parse(b.at) : NaN;
    const fa = Number.isFinite(ta);
    const fb = Number.isFinite(tb);
    if (fa && fb) return tb - ta;
    if (fa) return -1;
    if (fb) return 1;
    return 0;
  });
  return out.slice(0, MAX_HOPS);
}

/**
 * Pick the matching human GOLD approval verdict for a piece, if any. A candidate must be a
 * confirm/override for THIS piece (sourceEscalationId === id, and when the record names a
 * sourceCollection it must equal the piece's collection). Returns BOTH the shared-classifier
 * input ({decision,by,ts} — fed to deriveProvenance's dormant gold path) and the name-only
 * reference block. Returns null when none matches.
 */
export function pickGold(
  goldRecords: RawGoldDoc[] | undefined,
  id: string,
  collection: string,
  identities?: IdentityMap,
): { verdict: { decision: string; by: string; ts: string | null }; ref: ReceiptGold } | null {
  if (!Array.isArray(goldRecords)) return null;
  // Newest-first so we surface the latest human verdict.
  const candidates = goldRecords
    .filter((g) => {
      if (!g || typeof g !== 'object') return false;
      if (g.sourceEscalationId !== id) return false;
      if (g.sourceCollection && g.sourceCollection !== collection) return false;
      return typeof g.decision === 'string' && GOLD_APPROVAL_DECISIONS.has(g.decision);
    })
    .sort((a, b) => {
      const ta = Date.parse(toIso(a.ts) ?? '');
      const tb = Date.parse(toIso(b.ts) ?? '');
      const fa = Number.isFinite(ta);
      const fb = Number.isFinite(tb);
      if (fa && fb) return tb - ta;
      if (fa) return -1;
      if (fb) return 1;
      return 0;
    });
  const g = candidates[0];
  if (!g) return null;
  const at = toIso(g.ts);
  const identity = identityForUid(g.by, identities);
  return {
    verdict: { decision: g.decision as string, by: g.by as string, ts: at },
    // Reference block: id + decision + at always; `by` ONLY as a resolved masthead NAME (never a uid).
    ref: { id: g.id, decision: g.decision as string, at, ...(identity ? { by: identity.name } : {}) },
  };
}

/** The collection a receipt type reads. */
export function collectionForType(type: ReceiptType): string {
  return type === 'breaking' ? 'breaking_news' : 'articles';
}

/** The public canonical URL for a piece (mirrors the AI routes' URL construction). */
function canonicalUrlFor(doc: RawDoc, type: ReceiptType, siteUrl: string): string | null {
  const slug = typeof doc.slug === 'string' ? doc.slug.trim() : '';
  if (!slug) return null;
  const base = siteUrl.replace(/\/$/, '');
  if (type === 'breaking') return `${base}/breaking/${slug}/`;
  const contentType = typeof doc.contentType === 'string' && doc.contentType ? doc.contentType : 'news';
  return `${base}/${contentType}/${slug}/`;
}

/** The relative MACHINE receipt path — the raw JSON. AI routes emit this as `receiptUrl`. */
export function receiptPath(type: ReceiptType, id: string): string {
  return `/api/receipts/${type}/${encodeURIComponent(id)}/`;
}

/**
 * The relative HUMAN receipt-page path — the plain-English provenance card the BYLINE links (the
 * page itself links the machine JSON via receiptPath). Same fail-closed eligibility as the JSON
 * route (both go through fetchReceiptPayload), so it is only ever linked for a receipt-eligible piece.
 */
export function receiptPagePath(type: ReceiptType, id: string): string {
  return `/receipts/${type}/${encodeURIComponent(id)}/`;
}

/**
 * buildReceipt — the CS1b core. PURE. Given a raw published doc (+ any matching gold records),
 * produces the receipt payload, or `null` when the doc is not receipt-eligible (fail-closed). The
 * route renders `null` as the single 404. Re-checks eligibility so it is safe to call directly.
 */
export function buildReceipt(params: {
  type: ReceiptType;
  id: string;
  doc: RawDoc | null | undefined;
  goldRecords?: RawGoldDoc[];
  /** The resolved masthead identity map (server-side). Absent → every human under-claims. */
  identities?: IdentityMap;
  /** Up to 3 resolved {slug,name} entities the piece is filed under (server-resolved; I/O-free here). */
  entities?: Array<{ slug: string; name: string }>;
  siteUrl: string;
  now?: Date;
}): ReceiptPayload | null {
  const { type, id, doc, goldRecords, identities, entities, siteUrl } = params;
  if (!isReceiptEligible(doc, type)) return null;
  const d = doc as RawDoc;

  const kind: ContentKind = type === 'breaking' ? 'breaking' : 'article';
  const collection = collectionForType(type);
  const gold = pickGold(goldRecords, id, collection, identities);

  // The honest provenance — the SAME classifier the byline + AI routes use, now with the gold
  // path wired (grammar A can be backed by a transition hop OR a matching human gold verdict).
  const prov = deriveProvenance(d, { kind, goldRecord: gold?.verdict, identities });

  const correction = toPublicCorrection((d as { correction?: unknown }).correction as never);

  const payload: ReceiptPayload = {
    version: RECEIPT_VERSION,
    found: true,
    _about: RECEIPT_ABOUT,
    type,
    id,
    producedBy: prov.producedBy.label,
    canonicalUrl: canonicalUrlFor(d, type, siteUrl),
    publishedAt: toIso((d as { publishedAt?: unknown }).publishedAt),
    generatedAt: (params.now ?? new Date()).toISOString(),
    title: safeState((d as { title?: unknown }).title) ?? null,
    // reviewedBy: present ONLY in grammar A (a proven human verdict). Absent = no proof.
    ...(prov.reviewedBy
      ? {
          reviewedBy: {
            name: prov.reviewedBy.name,
            handle: prov.reviewedBy.handle,
            url: prov.reviewedBy.url,
            at: prov.reviewedBy.at,
          },
        }
      : {}),
  };

  const hops = buildPublishTransition(d, identities);
  if (hops) payload.publishTransition = hops;
  if (gold) payload.goldRecord = gold.ref;
  if (correction) payload.corrections = correction;
  if (entities?.length) payload.entities = entities.slice(0, 3);

  return payload;
}
